import { IVec3, Vec3 } from "./Vec3";

export interface IPlane {
  n: IVec3;
  d: number;
}

export class Plane {
  static fromVector(n: IVec3, d: number): IPlane {
    return {
      n,
      d,
    };
  }

  static from(nx: number, ny: number, nz: number, d: number): IPlane {
    return {
      n: Vec3.from(nx, ny, nz),
      d,
    };
  }

  static fromPoints(a: IVec3, b: IVec3, c: IVec3): IPlane {
    const ab = Vec3.sub(b, a);
    const ac = Vec3.sub(c, a);
    const n = Vec3.crossNormalized(ab, ac);
    return Plane.fromVector(n, Vec3.dot(n, b));
  }

  static fromNormal(p: IVec3, n: IVec3): IPlane {
    return Plane.fromVector(n, Vec3.dot(Vec3.normalized(n), p));
  }

  static calcClosestPointOnPlane(plane: IPlane, v: IVec3): IVec3 {
    const t = Vec3.dot(plane.n, v) - plane.d;
    return Vec3.addScaled(v, plane.n, -t);
  }

  static calcDistanceToPlane(plane: IPlane, v: IVec3): number {
    const dot = Vec3.dot(plane.n, v);
    return dot - plane.d;
  }

  static calcSignedDistanceToPlane(plane: IPlane, v: IVec3): number {
    const planeP = this.calcClosestPointOnPlane(plane, v);
    return Vec3.dot(plane.n, Vec3.sub(v, planeP));
  }

  static project(plane: IPlane, v: IVec3): IVec3 {
    return Vec3.cross(plane.n, Vec3.cross(v, plane.n));
  }

  static calcY(plane: IPlane, x: number, z: number): number {
    return (plane.d - plane.n.x * x - plane.n.z * z) / plane.n.y;
  }

  static isNan(plane: IPlane): boolean {
    return Vec3.isNaN(plane.n);
  }
}
